/**
 * Content Manager - AI Assistant Component
 *
 * AI-powered content generation and improvement panel.
 */

import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  IconButton,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  Tooltip,
  alpha,
} from '@mui/material';
import {
  Sparkles,
  Lightbulb,
  PenTool,
  Type,
  Search,
  Share2,
  FileText,
  Maximize2,
  Copy,
  Check,
  ChevronDown,
  ChevronUp,
  Wand2,
  RefreshCw,
} from 'lucide-react';
import { aiService } from '../../services';
import { useConfigStore } from '../../stores';
import type { AIFeatureType, AIResponse } from '../../types';

// =============================================================================
// AI FEATURES
// =============================================================================

const AI_FEATURES: {
  type: AIFeatureType;
  label: string;
  description: string;
  icon: React.ReactNode;
  placeholder: string;
}[] = [
  {
    type: 'generate_ideas',
    label: 'Generate Ideas',
    description: 'Get creative content ideas for any topic',
    icon: <Lightbulb size={18} />,
    placeholder: 'Enter a topic or theme to generate ideas for...',
  },
  {
    type: 'improve_content',
    label: 'Improve Content',
    description: 'Enhance your existing content',
    icon: <PenTool size={18} />,
    placeholder: 'Paste your content here to improve...',
  },
  {
    type: 'generate_headlines',
    label: 'Generate Headlines',
    description: 'Create compelling headlines',
    icon: <Type size={18} />,
    placeholder: 'Enter your topic or article summary...',
  },
  {
    type: 'generate_seo',
    label: 'SEO Optimization',
    description: 'Generate SEO-optimized meta content',
    icon: <Search size={18} />,
    placeholder: 'Paste your content to generate SEO elements...',
  },
  {
    type: 'generate_social',
    label: 'Social Posts',
    description: 'Create platform-optimized social content',
    icon: <Share2 size={18} />,
    placeholder: 'Enter your content or topic...',
  },
  {
    type: 'summarize',
    label: 'Summarize',
    description: 'Create concise summaries',
    icon: <FileText size={18} />,
    placeholder: 'Paste content to summarize...',
  },
  {
    type: 'expand',
    label: 'Expand Content',
    description: 'Expand brief content into detailed pieces',
    icon: <Maximize2 size={18} />,
    placeholder: 'Enter brief content to expand...',
  },
];

// =============================================================================
// PROPS
// =============================================================================

interface AIAssistantProps {
  onApply?: (content: string) => void;
  initialInput?: string;
  compact?: boolean;
}

// =============================================================================
// COMPONENT
// =============================================================================

export const AIAssistant: React.FC<AIAssistantProps> = ({
  onApply,
  initialInput = '',
  compact = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(!compact);
  const [selectedFeature, setSelectedFeature] = useState<AIFeatureType>('generate_ideas');
  const [input, setInput] = useState(initialInput);
  const [tone, setTone] = useState<'professional' | 'casual' | 'formal' | 'friendly'>('professional');
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState<AIResponse | null>(null);
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);

  const accentColour = useConfigStore((state) => state.accentColour);
  const feature = AI_FEATURES.find((f) => f.type === selectedFeature)!;

  const handleGenerate = async () => {
    if (!input.trim()) return;

    setLoading(true);
    setResponse(null);

    const { data } = await aiService.generate({
      type: selectedFeature,
      input: input.trim(),
      context: { tone },
    });

    if (data) {
      setResponse(data);
    }

    setLoading(false);
  };

  const handleCopy = async (text: string, index: number) => {
    await navigator.clipboard.writeText(text);
    setCopiedIndex(index);
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  const handleApply = (text: string) => {
    if (onApply) {
      onApply(text);
    }
  };

  if (compact && !isExpanded) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 2,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          '&:hover': { bgcolor: alpha(accentColour, 0.05) },
        }}
        onClick={() => setIsExpanded(true)}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Sparkles size={20} color={accentColour} />
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            AI Assistant
          </Typography>
        </Box>
        <ChevronDown size={18} color="#9ca3af" />
      </Paper>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          bgcolor: alpha(accentColour, 0.05),
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Sparkles size={20} color={accentColour} />
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            AI Assistant
          </Typography>
          <Chip
            size="small"
            label="Powered by Claude"
            sx={{ bgcolor: alpha(accentColour, 0.1), color: accentColour }}
          />
        </Box>
        {compact && (
          <IconButton size="small" onClick={() => setIsExpanded(false)}>
            <ChevronUp size={18} />
          </IconButton>
        )}
      </Box>

      <Box sx={{ p: 2 }}>
        {/* Feature Selection */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
          {AI_FEATURES.map((f) => (
            <Chip
              key={f.type}
              icon={f.icon as React.ReactElement}
              label={f.label}
              onClick={() => setSelectedFeature(f.type)}
              variant={selectedFeature === f.type ? 'filled' : 'outlined'}
              sx={{
                bgcolor: selectedFeature === f.type ? accentColour : 'transparent',
                color: selectedFeature === f.type ? 'white' : 'text.primary',
                '&:hover': {
                  bgcolor: selectedFeature === f.type ? accentColour : alpha(accentColour, 0.1),
                },
              }}
            />
          ))}
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {feature.description}
        </Typography>

        {/* Input */}
        <TextField
          fullWidth
          multiline
          rows={4}
          placeholder={feature.placeholder}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          sx={{ mb: 2 }}
        />

        {/* Options */}
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Tone</InputLabel>
            <Select
              value={tone}
              label="Tone"
              onChange={(e) => setTone(e.target.value as typeof tone)}
            >
              <MenuItem value="professional">Professional</MenuItem>
              <MenuItem value="casual">Casual</MenuItem>
              <MenuItem value="formal">Formal</MenuItem>
              <MenuItem value="friendly">Friendly</MenuItem>
            </Select>
          </FormControl>

          <Button
            variant="contained"
            startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <Wand2 size={18} />}
            onClick={handleGenerate}
            disabled={loading || !input.trim()}
            sx={{
              bgcolor: accentColour,
              '&:hover': { bgcolor: accentColour, filter: 'brightness(0.9)' },
            }}
          >
            {loading ? 'Generating...' : 'Generate'}
          </Button>

          {response && (
            <Button
              variant="outlined"
              startIcon={<RefreshCw size={18} />}
              onClick={handleGenerate}
              disabled={loading}
            >
              Regenerate
            </Button>
          )}
        </Box>

        {/* Results */}
        {response && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Suggestions
            </Typography>
            <List disablePadding>
              {response.suggestions.map((suggestion: string, index: number) => (
                <ListItem
                  key={index}
                  sx={{
                    bgcolor: '#f9fafb',
                    borderRadius: 1,
                    mb: 1,
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                  }}
                >
                  <ListItemText
                    primary={
                      <Typography
                        variant="body2"
                        sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}
                      >
                        {suggestion}
                      </Typography>
                    }
                  />
                  <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                    <Tooltip title={copiedIndex === index ? 'Copied!' : 'Copy'}>
                      <IconButton
                        size="small"
                        onClick={() => handleCopy(suggestion, index)}
                      >
                        {copiedIndex === index ? (
                          <Check size={16} color="#10b981" />
                        ) : (
                          <Copy size={16} />
                        )}
                      </IconButton>
                    </Tooltip>
                    {onApply && (
                      <Button
                        size="small"
                        onClick={() => handleApply(suggestion)}
                        sx={{ color: accentColour }}
                      >
                        Apply
                      </Button>
                    )}
                  </Box>
                </ListItem>
              ))}
            </List>

            {response.metadata && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Generated using {response.metadata.model} • {response.metadata.tokensUsed} tokens
              </Typography>
            )}
          </>
        )}
      </Box>
    </Paper>
  );
};

export default AIAssistant;
